/**
 * Which steps a PUBLIC (unauthenticated) customer is allowed to see.
 *
 * The public runtime (`/ext/workflows/{id}/new`, spec §2/§12) walks the same
 * workflow an advisor does, but it has no session, sends nothing to BMO and
 * triggers no integration — PDF generation is the only server call it makes.
 * Two step types cannot survive those constraints and are withheld outright.
 *
 * This lives in `form-core` rather than in `FormShell` because it is a rule
 * about the template, not about the React tree: it is a pure predicate over a
 * step, it is the thing that needs a test, and `PdfFunction` will need it too
 * the day the flag below can hide a `DynamicForm` step (see
 * {@link isPublicHiddenStep}).
 */

import type { WorkflowField, WorkflowStep, WorkflowStepType } from './templateTypes';

/**
 * Step types a public customer never sees.
 *
 * Both are advisor-and-bank machinery rather than customer questions:
 *
 * - `AdditionalDocuments` — uploads land in the customer-PII bucket under a
 *   spec §14.1 delivery name built from the application id BMO mints on the
 *   first AUTHENTICATED save (ADR-0019 / ADR-0026). A public flow has neither
 *   the id nor the save, and the upload route is advisor-gated server-side
 *   regardless, so the step could only ever render as a dead dropzone.
 * - `OneSpanESign` — e-signing is entirely BMO's downstream responsibility.
 *   The step also fires a paid ZeroBounce verification on entry and composes a
 *   signer roster carrying names, emails and phone numbers, none of which a
 *   public flow has anywhere to send.
 *
 * A Set rather than a chain of `||` so the rule reads as data and a future
 * addition is one line.
 */
export const PUBLIC_HIDDEN_STEP_TYPES: ReadonlySet<WorkflowStepType> = new Set<WorkflowStepType>([
  'AdditionalDocuments',
  'OneSpanESign',
]);

/**
 * Is this step withheld from a public customer?
 *
 * Two independent reasons, and the `||` is the whole shape of the rule:
 *
 *  - **The type cannot survive the public runtime** — see
 *    {@link PUBLIC_HIDDEN_STEP_TYPES}. Not an authoring choice.
 *  - **The originator marked it `advisorOnly`** — spec §12's per-step checkbox.
 *    This is the flag ADR-0031 recorded as missing everywhere; it now exists on
 *    the C# domain, in the DynamoDB serializer and on {@link WorkflowStep}, and
 *    it is read HERE and nowhere else. Only `DynamicForm` steps can carry it
 *    (the domain refuses it on any other type), because every other type is
 *    either structural — a public customer with no PDF generation step has no
 *    ending — or already withheld by the set above.
 *
 * That second term is why this takes a {@link WorkflowStep} rather than being a
 * predicate over {@link WorkflowStepType}.
 *
 * THE THING THAT CHANGED when the flag arrived: neither type in the set can
 * hold answers (`AdditionalDocuments` and `OneSpanESign` have no fields) or PDF
 * templates (`fileEntries` is only ever populated on `PdfGeneration`), which is
 * the only reason the answer-pruning walk used to be able to ignore audience
 * altogether. `advisorOnly` hides a `DynamicForm` step, so it breaks that
 * assumption — and `visibleValues` now takes an audience and drops a hidden
 * step's answers for exactly this reason. Without it a customer's answers to a
 * step they never saw would print into their PDF.
 */
export function isPublicHiddenStep(step: Pick<WorkflowStep, 'type' | 'advisorOnly'>): boolean {
  return PUBLIC_HIDDEN_STEP_TYPES.has(step.type) || step.advisorOnly === true;
}

/** Who a form runtime is being rendered for. */
export type StepAudience = 'internal' | 'public';

/**
 * Is this step eligible for THIS audience, conditions aside?
 *
 * Deliberately separate from `evaluateConditions`, and composed with it at the
 * call site rather than folded into it. Conditions are a function of the
 * answers and flip live as the customer types; audience eligibility is fixed
 * for the life of the runtime. Keeping them apart is what leaves
 * `evaluateConditions` a pure function of `(conditions, values)`, which is
 * what lets `PdfFunction` share it unchanged (ADR-0031).
 *
 * `'internal'` covers every authenticated audience — advisor runtime and
 * originator/approver preview alike. None of them hides anything by audience,
 * so they share one value rather than each getting their own.
 */
export function isStepEligibleForAudience(
  step: Pick<WorkflowStep, 'type' | 'advisorOnly'>,
  audience: StepAudience,
): boolean {
  return audience === 'internal' || !isPublicHiddenStep(step);
}

/** The field properties that make a field advisor-sourced. Structural rather than
 *  `Pick<WorkflowField, …>` so the front end's own `AdvisorPrefilledField` (which
 *  carries `readOnly` too) satisfies it without a cast. */
export type AdvisorSourcedField = Pick<WorkflowField, 'sourceValue' | 'advisorRepCodeSelector'>;

/**
 * Does this field draw its value from the SIGNED-IN ADVISOR's profile?
 *
 * Two authored properties say so, and they are deliberately not one:
 *
 *  - `sourceValue` starting `advisor.` — the text prefills (full name, email,
 *    phone, dealer code, …), resolved from the session when the step mounts.
 *  - `advisorRepCodeSelector` — a SingleSelect whose options ARE the session's
 *    representative codes. It carries no `sourceValue` of its own, but it is
 *    session-sourced by every other measure.
 *
 * `application.language` is excluded on purpose: it is the CLIENT's choice made
 * on the Welcome page, not a fact about the advisor, and a public customer
 * answers it exactly as an advisor-led one does.
 *
 * This is the predicate the front end's `advisorSourceKeyOf` is built on, so the
 * two can never disagree about what counts.
 */
export function isAdvisorSourcedField(field: AdvisorSourcedField): boolean {
  return (
    field.advisorRepCodeSelector === true || field.sourceValue?.startsWith('advisor.') === true
  );
}

/**
 * Is this field eligible for THIS audience?
 *
 * A PUBLIC customer has no session, so there is no advisor profile to resolve
 * against and an advisor-sourced field can only ever render broken: the text
 * prefills come up as empty disabled inputs, and an `advisorRepCodeSelector`
 * comes up as an EMPTY dropdown that the domain forces to be Required and
 * refuses to let be read-only — so it is not covered by the read-only
 * required-exemption and blocks Next forever. Withholding the field is the only
 * answer that leaves the form fillable.
 *
 * Same split as {@link isStepEligibleForAudience}, for the same reason:
 * eligibility is fixed for the life of the runtime, conditions flip live, so the
 * two are composed at the call site rather than folded together.
 */
export function isFieldEligibleForAudience(
  field: AdvisorSourcedField,
  audience: StepAudience,
): boolean {
  return audience === 'internal' || !isAdvisorSourcedField(field);
}

/** An empty set, shared. `withheldFieldIds` returns it for every internal
 *  audience, which is the overwhelmingly common case — allocating a fresh empty
 *  Set per render of every step would be pure churn. Never mutated. */
const NO_WITHHELD_FIELDS: ReadonlySet<string> = new Set<string>();

/**
 * Every field id on this step that THIS audience must not see.
 *
 * A precomputed set rather than a per-field predicate call, because the
 * consumers are the three walks that each visit every field on the step — the
 * renderer's section walk, `isStepComplete` and `missingRequiredFields` — and
 * they must agree exactly or Next blocks on a field that renders nowhere. One
 * set, built once per walk, is the same shape the front end already uses for
 * `hiddenBorrowedFieldIds`, and the three sites read identically because of it.
 *
 * Returns a shared empty set for `'internal'`, so the walks pay nothing at all
 * on the authenticated path.
 */
export function withheldFieldIds(
  step: Pick<WorkflowStep, 'sections'>,
  audience: StepAudience,
): ReadonlySet<string> {
  if (audience === 'internal') return NO_WITHHELD_FIELDS;
  const out = new Set<string>();
  for (const section of step.sections) {
    for (const row of section.rows) {
      for (const column of row.columns) {
        const field = column.field;
        if (field && isAdvisorSourcedField(field)) out.add(field.id);
      }
    }
  }
  return out;
}
